import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, ArrowRight, ArrowLeft, ShieldCheck } from 'lucide-react';
import { toast } from 'react-toastify';
import authService from '../../services/authService';

const ForgotPassword = () => {
    const navigate = useNavigate();
    const LogoImg = '/logo.png';
    
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    
    const [otpSent, setOtpSent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [otpLoading, setOtpLoading] = useState(false);
    const [countdown, setCountdown] = useState(0);

    useEffect(() => {
        let timer;
        if (countdown > 0) {
            timer = setInterval(() => {
                setCountdown((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [countdown]);

    const handleSendOtp = async () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email || !emailRegex.test(email)) {
            toast.warn('Vui lòng nhập địa chỉ email hợp lệ!');
            return;
        }

        setOtpLoading(true);
        try {
            // Tạm thời gọi chung API gửi mã của phần đăng ký
            await authService.sendEmailOtp(email);
            setOtpSent(true);
            setCountdown(60);
            toast.success('Mã OTP đã được gửi đến Email của bạn!');
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Không thể gửi OTP!';
            toast.error(typeof errorMessage === 'string' ? errorMessage : 'Lỗi hệ thống!');
        } finally {
            setOtpLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!otpSent) {
            toast.warn('Vui lòng gửi mã OTP trước!');
            return;
        }
        if (otp.length < 6) {
            toast.warn('Vui lòng nhập đủ mã OTP!');
            return;
        }
        if (newPassword.length < 6) {
            toast.warn('Mật khẩu mới phải có ít nhất 6 ký tự!');
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.warn('Mật khẩu xác nhận không khớp!');
            return;
        }

        setLoading(true);
        try {
            // TODO: Cần gọi API reset mật khẩu ở đây (chưa có ở Backend)
            // await authService.resetPassword({ email, otp, newPassword });
            
            toast.success('Đặt lại mật khẩu thành công! Vui lòng đăng nhập.');
            navigate('/login');
        } catch (error) {
            toast.error('Mã OTP không đúng hoặc có lỗi xảy ra!');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-orange-50/30 flex flex-col justify-center items-center p-4 relative">
            <Link to="/login" className="absolute top-6 left-6 flex items-center gap-2 text-gray-500 hover:text-tnc-orange transition-colors font-medium">
                <ArrowLeft size={20} />
                Quay lại đăng nhập
            </Link>

            <div className="w-full max-w-md bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden border border-gray-100">
                <div className="bg-white p-6 text-center relative overflow-hidden flex flex-col items-center border-b border-gray-50">
                    <div className="absolute top-0 left-0 w-full h-1.5 bg-tnc-orange"></div>
                    <img src={LogoImg} alt="Logo" className="h-20 w-auto object-contain mb-2" />
                    <h2 className="font-bold text-gray-700 text-lg mt-2">KHÔI PHỤC MẬT KHẨU</h2>
                </div>

                <div className="p-8 pt-6">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                                    <Mail size={18} />
                                </div>
                                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={otpSent}
                                    className="w-full pl-10 pr-[100px] py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange text-sm transition-colors bg-gray-50/50 disabled:bg-gray-100"
                                    placeholder="Địa chỉ Email của bạn *"
                                />
                                <button type="button" onClick={handleSendOtp} disabled={otpLoading || !email || countdown > 0}
                                    className="absolute right-1 top-1 bottom-1 px-3 bg-blue-50 text-blue-600 rounded-md text-xs font-bold hover:bg-blue-100 disabled:opacity-50 min-w-[90px]"
                                >
                                    {otpLoading ? 'ĐANG GỬI...' : countdown > 0 ? `GỬI LẠI (${countdown}s)` : otpSent ? 'GỬI LẠI MÃ' : 'GỬI MÃ'}
                                </button>
                            </div>

                            {otpSent && (
                                <div className="relative animate-fade-in-down">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                                        <ShieldCheck size={18} className="text-tnc-orange" />
                                    </div>
                                    <input type="text" value={otp} onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))} maxLength={6} required
                                        className="w-full pl-10 pr-3 py-2.5 border border-orange-300 rounded-lg focus:outline-none focus:border-tnc-orange text-sm bg-orange-50/30"
                                        placeholder="Nhập mã OTP 6 số *"
                                    />
                                </div>
                            )}
                        </div>

                        {otpSent && (
                            <>
                                <div className="relative animate-fade-in-down">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400"><Lock size={18} /></div>
                                    <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required
                                        className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:border-tnc-orange text-sm bg-gray-50/50"
                                        placeholder="Mật khẩu mới *"
                                    />
                                </div>
                                <div className="relative animate-fade-in-down">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400"><Lock size={18} /></div>
                                    <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required
                                        className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:border-tnc-orange text-sm bg-gray-50/50"
                                        placeholder="Xác nhận mật khẩu mới *"
                                    />
                                </div>
                            </>
                        )}

                        <button type="submit" disabled={loading || !otpSent}
                            className="w-full bg-tnc-orange hover:bg-tnc-orange-hover text-white font-bold py-3 rounded-lg transition-all flex justify-center items-center gap-2 mt-4 disabled:bg-gray-400"
                        >
                            {loading ? <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span> : <>ĐẶT LẠI MẬT KHẨU <ArrowRight size={18} /></>}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;